import { useEffect, useMemo, useRef, useState } from 'react';
import { useCalendar } from '../CalendarContext';
import { useTeamMembers } from '../../settings/hooks/useTeam';
import { useAllUsers } from '../../../shared/hooks/useAllUsers';

// 04-enforce.md 4.1 -- extraído de NewEvent.jsx. Responsabilidad única: todo el estado del
// formulario de evento + su carga inicial (modo crear vs. editar, resolución del organizador,
// invitados) + los handlers de guardado/borrado/rechazo. El JSX (y sus 3 sub-campos:
// calendario, invitados, recurrencia) se queda en los componentes presentacionales. El contrato
// de `onSave` (objeto plano en camelCase) es idéntico -- ver `NewEvent.test.jsx`.

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: hidrata el formulario de evento desde `CalendarContext`/`initialData` al abrir en modo edición.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const toLocalIso = (ms) => {
  const d = new Date(ms);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export function useNewEventForm({ isOpen, initialData, onUpdate, onSave, onDelete, user }) {
  const { calendars: contextCalendars } = useCalendar();

  const [title, setTitle] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [description, setDescription] = useState('');
  const descriptionRef = useRef(null);

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('none');
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);

  const [guests, setGuests] = useState([]);
  const [guestInput, setGuestInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  // 02.5-calendar.md a.5 -- hooks compartidos (React Query dedup) en vez de un fetch crudo
  // propio. Ambas ramas (space / global) se normalizan igual: los objetos de
  // `/api/space/:id/members` no traen `name`/`image`, solo `nick`/`link-profile-img-thumb`.
  const spaceId = user?.currentSpace?.id;
  const teamMembersQuery = useTeamMembers(spaceId);
  const allUsersQuery = useAllUsers();
  const allMembers = useMemo(() => {
    const raw = spaceId ? (teamMembersQuery.data || []) : (allUsersQuery.data || []);
    return (Array.isArray(raw) ? raw : []).map((u) => ({
      id: u.id,
      name: u.nick || u.name || 'Unknown',
      email: u.email || '',
      image: (u['link-profile-img-thumb'] || u['link-profile-img']) || u.image,
    }));
  }, [spaceId, teamMembersQuery.data, allUsersQuery.data]);

  const [isInvited, setIsInvited] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  const [calendars, setCalendars] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const [isCalDropdownOpen, setIsCalDropdownOpen] = useState(false);

  // La lista de calendarios ya la mantiene CalendarContext (mismo fetch + orden
  // personal-primero) -- se deriva de ahí en vez de volver a pedirla.
  useEffect(() => {
    if (contextCalendars.length > 0) {
      setCalendars(contextCalendars);
      if (!selectedCalendarId) {
        const personalCal = contextCalendars.find(c => c.name === 'Personal' && String(c.user_id) === String(user?.dbId));
        setSelectedCalendarId(personalCal ? personalCal.id : contextCalendars[0].id);
      }
    }
  }, [contextCalendars, user?.dbId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (recurrenceType === 'weekly' && selectedWeekDays.length === 0 && start) {
      const day = new Date(start).getDay();
      if (!isNaN(day)) setSelectedWeekDays([day]);
    }
  }, [recurrenceType, start]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    if (initialData && initialData.id) {
      // Edit mode
      setTitle(initialData.title || '');
      setStart(initialData.start || '');
      setEnd(initialData.end || '');
      setDescription(initialData.description || '');
      if (initialData.calendarId) setSelectedCalendarId(initialData.calendarId);

      if (initialData.recurrence && initialData.recurrence.type !== 'none') {
        setIsRecurring(true);
        setRecurrenceType(initialData.recurrence.type);
        setSelectedWeekDays(initialData.recurrence.days || []);
      } else {
        setIsRecurring(false);
        setRecurrenceType('none');
        setSelectedWeekDays([]);
      }

      if (descriptionRef.current) {
        descriptionRef.current.innerHTML = initialData.description || '';
      }

      const loadedGuests = initialData.attendees && Array.isArray(initialData.attendees) ? [...initialData.attendees] : [];
      const ownerId = initialData.createdBy || initialData.created_by;
      if (ownerId) {
        const sOwnerId = String(ownerId);
        const alreadyHasOwner = loadedGuests.some(g => g.isOwner || (g.id && String(g.id) === sOwnerId));
        if (!alreadyHasOwner) {
          let ownerObj = null;
          if (user && String(user.dbId) === sOwnerId) {
            ownerObj = {
              name: user.nick || user.name,
              email: user.email,
              avatar: (user['link-profile-img-thumb'] || user['link-profile-img']) || user.image,
              id: user.dbId,
            };
          } else if (allMembers.length > 0) {
            const m = allMembers.find(u => String(u.id) === sOwnerId);
            if (m) ownerObj = { name: m.name, email: m.email, avatar: m.image, id: m.id };
          }
          if (ownerObj) loadedGuests.unshift({ ...ownerObj, isOwner: true });
        }
      }
      setGuests(loadedGuests);
      setIsInvited(!!(initialData.createdBy && user && String(initialData.createdBy) !== String(user.dbId)));
    } else {
      // Create mode
      if (initialData) {
        setStart(initialData.start || '');
        setEnd(initialData.end || '');
      } else {
        setTitle('');
        setDescription('');
      }
      setIsRecurring(false);
      setRecurrenceType('none');
      setSelectedWeekDays([]);
      if (descriptionRef.current) descriptionRef.current.innerHTML = initialData?.description || '';

      if (calendars.length > 0) {
        let defaultCalId = calendars[0].id;
        if (user?.calendar_personal) {
          const directMatch = calendars.find(c => c.id === user.calendar_personal);
          if (directMatch) defaultCalId = directMatch.id;
        } else {
          const personalCal = calendars.find(c => c.name === 'Personal' && String(c.user_id) === String(user?.dbId));
          if (personalCal) defaultCalId = personalCal.id;
        }
        setSelectedCalendarId(defaultCalId);
      }

      if (user && guests.length === 0) {
        setGuests([{
          name: user.nick || user.name,
          email: user.email,
          avatar: (user['link-profile-img-thumb'] || user['link-profile-img']) || user.image || user.photoURL,
          id: user.dbId,
          isOwner: true,
        }]);
      }
      setIsInvited(false);
    }
    setShowDeclineConfirm(false);
  }, [isOpen, initialData, user, calendars, allMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live update parent when fields change
  useEffect(() => {
    if (onUpdate) {
      onUpdate({ title, start, end, description, attendees: guests, calendarId: selectedCalendarId });
    }
  }, [title, start, end, description, guests, selectedCalendarId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDuration = (mins) => {
    if (!start || !end) return false;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (isNaN(s) || isNaN(e)) return false;
    return Math.round((e - s) / 60000) === mins;
  };

  const handleDuration = (minutes) => {
    if (!start) return;
    const startDate = new Date(start);
    if (isNaN(startDate.getTime())) return;
    setEnd(toLocalIso(startDate.getTime() + minutes * 60000));
  };

  const handleInternalSave = () => {
    if (!onSave) return;
    onSave({
      id: initialData?.id,
      title,
      start,
      end,
      description,
      attendees: guests.filter(g => !g.isOwner),
      calendarId: selectedCalendarId,
      recurrence: isRecurring ? {
        type: recurrenceType,
        days: recurrenceType === 'weekly' ? selectedWeekDays : undefined,
      } : null,
      actorId: user.dbId,
    });
  };

  const handleDelete = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (onDelete && initialData?.id) onDelete(initialData.id);
  };

  const handleDeclineClick = (e) => {
    if (e) e.stopPropagation();
    setShowDeclineConfirm(true);
  };
  const confirmDecline = (e) => {
    if (e) e.stopPropagation();
    const uEmail = (user.email || '').toLowerCase();
    const updatedGuests = guests.filter(g => !g.isOwner && (g.email || '').toLowerCase() !== uEmail);
    if (onSave) onSave({ title, start, end, description, attendees: updatedGuests, calendarId: selectedCalendarId });
  };
  const cancelDecline = (e) => {
    if (e) e.stopPropagation();
    setShowDeclineConfirm(false);
  };

  return {
    // fields
    title, setTitle, start, setStart, end, setEnd, description, setDescription, descriptionRef,
    isRecurring, setIsRecurring, recurrenceType, setRecurrenceType, selectedWeekDays, setSelectedWeekDays,
    guests, setGuests, guestInput, setGuestInput, suggestions, setSuggestions, allMembers,
    isInvited, showDeclineConfirm,
    calendars, selectedCalendarId, setSelectedCalendarId, isCalDropdownOpen, setIsCalDropdownOpen,
    // handlers
    isDuration, handleDuration, handleInternalSave, handleDelete,
    handleDeclineClick, confirmDecline, cancelDecline,
  };
}
